// @flow

import React, {Component} from 'react';
import Icon, {Icons} from '@pb/kit-component-icon';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {ArrowDirection, CUSTOM_SCROLL_EVENT} from './constants';
import {getArrowTopPosition} from './utils';
import styles from './scrollable.component.module.styl';

import type {ArrowDirectionType} from './constants';
import type {ScrollArrowData} from './typedef';


const classNames = withCssModule(styles);
const classes = hashedClasses(styles);


const TYPE_CLASSES = {
    [ArrowDirection.LEFT]: {
        icon: Icons.CHEVRON_LEFT,
        className: 'scrollable__arrow_left'
    },
    [ArrowDirection.RIGHT]: {
        icon: Icons.CHEVRON_RIGHT,
        className: 'scrollable__arrow_right'
    }
};

type Props = {|
    direction: ArrowDirectionType,
    onClick: () => void,
    parent: HTMLDivElement | null,
    activeScroll: boolean,
    ...ScrollArrowData,
|};

/**
 *  @deprecated It will be removed in 6 months 09-15-2025. use `@pb/kit-component-native-scroll`
 *  */
export class ScrollableArrow extends Component<Props> {
    arrow: ?HTMLDivElement;
    arrowIcon: ?HTMLElement;
    customScrollAnimationFrame: ?AnimationFrameID = null;

    static defaultProps = {
        activeScroll: false
    };

    componentDidMount() {
        if (this.arrow) {
            this.arrowIcon = this.arrow.querySelector(`.${styles['scrollable__arrow-icon']}`);
            this.handleScroll();

            if (this.props.customScrollable) {
                this.props.customScrollable.addEventListener(CUSTOM_SCROLL_EVENT, this.handleCustomScroll);
            } else {
                window.addEventListener('scroll', this.handleScroll);
            }
            window.addEventListener('resize', this.handleScroll);
        }
    }

    componentDidUpdate() {
        this.handleScroll();
    }

    componentWillUnmount() {
        if (this.props.customScrollable) {
            this.props.customScrollable.removeEventListener(CUSTOM_SCROLL_EVENT, this.handleCustomScroll);
        } else {
            window.removeEventListener('scroll', this.handleScroll);
        }
        window.removeEventListener('resize', this.handleScroll);
    }

    handleCustomScroll = () => {
        if (this.customScrollAnimationFrame) {
            cancelAnimationFrame(this.customScrollAnimationFrame);
        }
        this.customScrollAnimationFrame = requestAnimationFrame(this.handleScroll);
    }

    isElementSticky = (elem: HTMLElement, stickyClassName: string) => {
        return elem.classList.contains(stickyClassName.replace('.', ''));
    }

    getNavigationHeight = () => {
        const {navClasses, navigationContainer} = this.props;

        if (navClasses && navigationContainer) {
            const {nav, stickyNav} = navClasses;
            const navigation = navigationContainer.querySelector(nav);
            if (navigation) {
                return this.isElementSticky(navigation, stickyNav) ? navigation.offsetHeight : 0;
            }

            return 0;
        }
        return 0;
    };

    getHeaderData = () => {
        const {headerClasses, parent} = this.props;
        const defaultHeaderData = {
            headerHeight: 0,
            headerTopOffset: 0,
            frozenHeaderHeight: 0
        };

        if (headerClasses) {
            const header = parent && parent.querySelector(headerClasses.headers);
            if (header) {
                const {height: headerHeight, top: headerTopOffset} = header.getBoundingClientRect();

                return {
                    headerHeight,
                    headerTopOffset,
                    frozenHeaderHeight: this.isElementSticky(header, headerClasses.frozenHeader) ? headerHeight : 0
                };
            }
            return defaultHeaderData;
        }

        return defaultHeaderData;
    };

    handleScroll = () => {
        const {customScrollable} = this.props;

        const arrow = this.arrow;
        const arrowIcon = this.arrowIcon;

        if (arrow && arrowIcon) {
            const {top: arrowHolderTop, height: arrowHolderHeight} = arrow.getBoundingClientRect();

            const containerBottom = customScrollable ? customScrollable.getBoundingClientRect().bottom : 0;

            const clientHeight = document.documentElement?.clientHeight || 0;

            const top = getArrowTopPosition({
                arrowHolderHeight,
                arrowHolderTop,
                containerBottom: containerBottom || clientHeight,
                stickyNavHeight: this.getNavigationHeight(),
                ...this.getHeaderData()
            });
            arrowIcon.style.top = `${top}px`;
        }
    };

    onRef = (el: ?HTMLDivElement) => {
        this.arrow = el;
    };

    render() {
        const {direction, activeScroll} = this.props;
        const arrowClasses = classNames('scrollable__arrow',
            TYPE_CLASSES[direction].className, {
                scrollable__arrow_active: activeScroll
            });

        return (
            <div onClick={this.props.onClick} ref={this.onRef} className={arrowClasses}>
                <Icon mix={classes['scrollable__arrow-icon']} type={TYPE_CLASSES[direction].icon}/>
            </div>
        );
    }
}
